import { z } from "zod";

import {
  BRIDGE_FETCH_MAX_BYTES,
  zBridgeFetchChunk,
  zBridgeFetchEnd,
  zBridgeFetchError,
  zBridgeFetchHead,
  zBridgeHttpError,
  zBridgeHttpResponse,
  zBridgeQueryError,
  zBridgeQueryResponse,
  zBridgeSecretReadError,
  zBridgeSecretReadResponse,
  zBridgeTunnelError,
  zBridgeTunnelOpen,
} from "./bridge";

// What API tasks pass among themselves about a bridge. Kept out of `./bridge`,
// which the customer-run bridge imports, so none of this ships in its image.

/**
 * The API's relay of a bridge's `fetch_end`. `uploaded` marks a call whose body
 * another API task took as an upload: the end then carries no body, and the
 * call finishes on that task's `fetch_stored` or failure.
 */
export const zBridgeFetchEndReply = zBridgeFetchEnd.extend({
  uploaded: z.literal(true).optional(),
});
export type BridgeFetchEndReply = z.infer<typeof zBridgeFetchEndReply>;

/** Staging prefix in the files bucket; a bucket lifecycle rule expires whatever no caller adopted. */
export const BRIDGE_UPLOAD_STORAGE_PREFIX = "bridge-uploads/";

/**
 * Published by whichever API task received the bridge's upload, once the body
 * is in object storage. Never sent by a bridge. The key pattern confines a reply
 * to the staging prefix, so a caller that adopts or deletes the object can
 * never be pointed at a workspace file.
 */
export const zBridgeFetchStored = z.object({
  type: z.literal("fetch_stored"),
  id: z.string().min(1),
  storageKey: z
    .string()
    .regex(/^bridge-uploads\/[0-9a-f-]{36}\/[0-9a-f-]{36}$/)
    .max(200),
  bytes: z.number().int().min(0).max(BRIDGE_FETCH_MAX_BYTES),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
});
export type BridgeFetchStored = z.infer<typeof zBridgeFetchStored>;
export type BridgeStoredBody = Omit<BridgeFetchStored, "type" | "id">;

/** Heartbeat while an upload streams into storage, so the caller's idle timeout measures progress. */
export const zBridgeFetchProgress = z.object({
  type: z.literal("fetch_progress"),
  id: z.string().min(1),
  bytes: z.number().int().min(0),
});
export type BridgeFetchProgress = z.infer<typeof zBridgeFetchProgress>;

export const zBridgeCallReply = z.discriminatedUnion("type", [
  zBridgeHttpResponse,
  zBridgeHttpError,
  zBridgeQueryResponse,
  zBridgeQueryError,
  zBridgeFetchHead,
  zBridgeFetchChunk,
  zBridgeFetchEndReply,
  zBridgeFetchError,
  zBridgeFetchStored,
  zBridgeFetchProgress,
  zBridgeTunnelOpen,
  zBridgeTunnelError,
  zBridgeSecretReadResponse,
  zBridgeSecretReadError,
]);
export type BridgeCallReply = z.infer<typeof zBridgeCallReply>;

export function isFinalBridgeReply(reply: BridgeCallReply): boolean {
  return (
    reply.type !== "fetch_head" &&
    reply.type !== "fetch_chunk" &&
    reply.type !== "fetch_stored" &&
    reply.type !== "fetch_progress"
  );
}

export type BridgeConnectionPresence = {
  id: string;
  version: string;
  connectedAt: string;
  lastSeenAt: string;
  capabilities: string[];
  allowed: string[];
  inflight?: number;
};

export type BridgePresence = {
  version: string;
  connectedAt: string;
  lastSeenAt: string;
  capabilities: string[];
  allowed: string[];
  connections?: BridgeConnectionPresence[];
};
